import { Unit } from '@rematter/frontend-lib-api-client';

import { LoadReconciliationRecord } from '../types/load';
import { toMoney, zeroMoney } from '../utils/money';
import { toWeight, zeroWeight } from '../utils/weight';

import { createQueryAtomFactory } from '~/shared/providers/api/createQueryAtomFactory';

export const loadReconciliationQueryAtom = createQueryAtomFactory(
  'trade.loadReconciliation',
  (params: { loadId: string }) => ({
    loadReconciliation: {
      __args: { loadId: params.loadId },
      id: true,
      loadId: true,
      ticketNumber: true,
      loadReceivedDate: true,
      ticketIssueDate: true,
      millReceivedWeightPostDate: true,
      externalNotes: true,
      internalNotes: true,
      invoiceId: true,
      invoiceType: true,
      files: {
        id: true,
        fileName: true,
        fileSize: true,
        mimeType: true,
        status: true,

        metadata: true,
        timestamps: {
          createdAt: true,
          updatedAt: true,
        },
      },
      receivedLineItems: {
        id: true,
        loadLineItemId: true,
        materialType: true,
        grossWeight: {
          value: true,
          unit: true,
        },
        tareWeight: {
          value: true,
          unit: true,
        },
        netWeight: {
          value: true,
          unit: true,
        },
        customerGrossWeight: {
          value: true,
          unit: true,
        },
        customerTareWeight: {
          value: true,
          unit: true,
        },
        notes: true,
        timestamps: {
          createdAt: true,
          updatedAt: true,
        },
        totalValue: {
          amount: true,
          currencyCode: true,
        },
        unitPrice: {
          amount: true,
          currencyCode: true,
        },
        pricingUnit: true,
        itemId: true,
      },
      deductions: {
        id: true,
        receivedLineItemId: true,
        deductionTypeId: true,
        weightQuantity: {
          value: true,
          unit: true,
        },
        priceQuantity: {
          amount: true,
          currencyCode: true,
        },
      },
      freightCharges: {
        id: true,
        hauler: true,
        freightLane: true,
        deliveryNumber: true,
        accruedAmount: {
          amount: true,
          currencyCode: true,
        },
        chargebackAmount: {
          amount: true,
          currencyCode: true,
        },
        notes: true,
      },
    },
  }),
  {
    select: (data): LoadReconciliationRecord | null => {
      if (!data?.loadReconciliation) {
        return null;
      }

      return {
        id: data.loadReconciliation.id,
        loadId: data.loadReconciliation.loadId,
        ticketNumber: data.loadReconciliation.ticketNumber,
        loadReceivedDate: data.loadReconciliation.loadReceivedDate,
        ticketIssueDate: data.loadReconciliation.ticketIssueDate,
        millReceivedWeightPostDate:
          data.loadReconciliation.millReceivedWeightPostDate,
        externalNotes: data.loadReconciliation.externalNotes,
        internalNotes: data.loadReconciliation.internalNotes,
        invoiceId: data.loadReconciliation.invoiceId,
        invoiceType: data.loadReconciliation.invoiceType,
        files:
          data.loadReconciliation.files?.map((attachment) => ({
            id: attachment.id,
            fileName: attachment.fileName,
            fileSize: attachment.fileSize,
            mimeType: attachment.mimeType,
            status: attachment.status,
            downloadUrl: undefined,
            metadata: attachment.metadata ?? undefined,
            timestamps: {
              createdAt: attachment.timestamps.createdAt,
              updatedAt: attachment.timestamps.updatedAt,
            },
          })) ?? [],
        receivedLineItems:
          data.loadReconciliation.receivedLineItems?.map((item) => ({
            id: item.id,
            loadLineItemId: item.loadLineItemId,
            materialType: item.materialType,
            grossWeight: item.grossWeight
              ? toWeight(item.grossWeight.value, item.grossWeight.unit)
              : null,
            tareWeight: item.tareWeight
              ? toWeight(item.tareWeight.value, item.tareWeight.unit)
              : null,
            netWeight: item.netWeight
              ? toWeight(item.netWeight.value, item.netWeight.unit)
              : zeroWeight(),
            customerGrossWeight: item.customerGrossWeight
              ? toWeight(
                  item.customerGrossWeight.value,
                  item.customerGrossWeight.unit,
                )
              : null,
            customerTareWeight: item.customerTareWeight
              ? toWeight(
                  item.customerTareWeight.value,
                  item.customerTareWeight.unit,
                )
              : null,
            unitPrice: item.unitPrice ? toMoney(item.unitPrice) : null,
            pricingUnit: item.pricingUnit as Unit,
            totalValue: item.totalValue
              ? toMoney(item.totalValue)
              : zeroMoney(),
            itemId: item.itemId,
            notes: item.notes,
            timestamps: {
              createdAt: item.timestamps.createdAt,
              updatedAt: item.timestamps.updatedAt,
            },
          })) ?? [],
        deductions:
          data.loadReconciliation.deductions?.map((deduction) => ({
            id: deduction.id,
            receivedLineItemId: deduction.receivedLineItemId,
            deductionTypeId: deduction.deductionTypeId,
            weightQuantity: deduction.weightQuantity
              ? toWeight(
                  deduction.weightQuantity.value,
                  deduction.weightQuantity.unit,
                )
              : null,
            priceQuantity: deduction.priceQuantity
              ? toMoney(deduction.priceQuantity)
              : null,
          })) ?? [],
        freightCharges:
          data.loadReconciliation.freightCharges?.map((charge) => ({
            id: charge.id,
            hauler: charge.hauler,
            freightLane: charge.freightLane,
            deliveryNumber: charge.deliveryNumber,
            accruedAmount: charge.accruedAmount
              ? toMoney(charge.accruedAmount)
              : zeroMoney(),
            chargebackAmount: charge.chargebackAmount
              ? toMoney(charge.chargebackAmount)
              : zeroMoney(),
            notes: charge.notes,
          })) ?? [],
      };
    },
  },
);
